import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface LicenseDevice {
  id: number;
  license_id: number;
  user_id: string | null;
  device_name: string;
  machine_code: string | null;
  os: string | null;
  location: string | null;
  status: string;
  activated_at: string | null;
  last_seen_at: string | null;
  released_at: string | null;
  release_source: string | null;
}

interface UseLicenseDevicesResult {
  devices: LicenseDevice[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  release: (deviceId: number) => Promise<void>;
  rename: (deviceId: number, name: string) => Promise<void>;
  activate: (input: { machineCode: string; deviceName?: string; os?: string; location?: string }) => Promise<void>;
  releaseInactive: (inactiveDays?: number) => Promise<{ released: number; activationCount: number }>;
}

async function invokeDevice<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("license-device", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

export function useLicenseDevices(licenseId: number | null): UseLicenseDevicesResult {
  const [devices, setDevices] = useState<LicenseDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!licenseId) {
      setDevices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("license_devices")
        .select("*")
        .eq("license_id", licenseId)
        .order("activated_at", { ascending: false });
      if (queryError) throw queryError;
      setDevices((data as LicenseDevice[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "设备信息加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [licenseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const release = useCallback(
    async (deviceId: number) => {
      await invokeDevice({ action: "release", deviceId });
      await load();
    },
    [load],
  );

  const rename = useCallback(
    async (deviceId: number, name: string) => {
      await invokeDevice({ action: "rename", deviceId, deviceName: name });
      await load();
    },
    [load],
  );

  const activate = useCallback(
    async (input: { machineCode: string; deviceName?: string; os?: string; location?: string }) => {
      await invokeDevice({ action: "activate", licenseId, ...input });
      await load();
    },
    [licenseId, load],
  );

  const releaseInactive = useCallback(
    async (inactiveDays = 30) => {
      const result = await invokeDevice<{ released?: number; activationCount?: number }>({
        action: "releaseInactive",
        licenseId,
        inactiveDays,
      });
      await load();
      return { released: result.released ?? 0, activationCount: result.activationCount ?? 0 };
    },
    [licenseId, load],
  );

  return { devices, loading, error, reload: load, release, rename, activate, releaseInactive };
}